import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, Clock } from "lucide-react";

type Detail = { plan?: string; source?: string; email?: string };
const EVENT = "lex:payments-unavailable";

export function openPaymentsUnavailable(detail: Detail = {}) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent<Detail>(EVENT, { detail }));
  }
}

export function PaymentsUnavailableDialog() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<Detail>({});
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent<Detail>).detail || {};
      setDetail(d);
      setEmail(d.email ?? "");
      setDone(false);
      setError(null);
      setOpen(true);
    };
    window.addEventListener(EVENT, onOpen);
    return () => window.removeEventListener(EVENT, onOpen);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const r = await fetch("/api/public/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          plan: detail.plan,
          source: detail.source ?? "payments-unavailable",
          kind: "waitlist",
          website,
          locale: i18n.resolvedLanguage?.startsWith("en") ? "en" : "it",
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || t("paymentsWip.error"));
      setDone(true);
    } catch (err: any) {
      setError(err?.message ?? t("paymentsWip.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/15 text-amber-500">
            <Clock className="h-5 w-5" />
          </div>
          <DialogTitle className="font-display">{t("paymentsWip.title")}</DialogTitle>
          <DialogDescription>{t("paymentsWip.desc")}</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <p className="text-sm">{t("paymentsWip.success")}</p>
            <Button onClick={() => setOpen(false)} variant="outline">{t("paymentsWip.close")}</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="pwip-email">{t("paymentsWip.emailLabel")}</Label>
              <Input id="pwip-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("paymentsWip.sending")}</> : t("paymentsWip.notify")}
            </Button>
            <p className="text-xs text-muted-foreground">{t("paymentsWip.note")}</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
